import { useState, useEffect } from 'react';
import { X, Calendar } from 'lucide-react';
import { useTranslation } from '../lib/LanguageContext';

interface SetDeadlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (deadline: string | null) => void;
  currentDeadline: string | null;
  todoText: string;
}

export function SetDeadlineModal({
  isOpen,
  onClose,
  onSave,
  currentDeadline,
  todoText
}: SetDeadlineModalProps) {
  const [deadline, setDeadline] = useState<string>(currentDeadline || '');
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      setDeadline(currentDeadline || '');
    }
  }, [isOpen, currentDeadline]);

  const handleSave = () => {
    onSave(deadline || null);
    onClose();
  };

  const handleClear = () => {
    onSave(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            {t('setDeadlineModalTitle')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          <div className="mb-4">
            <div className="font-medium text-gray-800 mb-2">{t('taskLabel')}</div>
            <div className="bg-indigo-50 p-3 rounded-md text-gray-700">
              {todoText}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-2">
              {t('deadlineDate')}
            </label>
            <input
              type="date"
              id="deadline"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              min={new Date().toISOString().split('T')[0]}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('selectDateForDeadline')}
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            {currentDeadline && (
              <button
                onClick={handleClear}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {t('clearDeadline')}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!deadline}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('setDeadlineButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}